import type { ProgressSummary, ProgressState } from "../engine";
import type { BdfzSession } from "./identity";
import { avatarUrl, displayName } from "./identity";

const BOARD_KEY = "lunyu-battle-leaderboard-v1";
const GUEST_KEY = "lunyu-battle-guest-v1"; // gitleaks:allow - localStorage namespace, not a credential
const ALIAS_KEY = "lunyu-battle-display-alias-v1";
const AVATAR_KEY = "lunyu-battle-avatar-v1";
const ACTIVE_NAME_KEY = "lunyu-battle-active-name-v1";
const ACTIVE_AVATAR_KEY = "lunyu-battle-active-avatar-v1";
export const PLAYER_NAME_EVENT = "lunyu-player-name";
export const PLAYER_AVATAR_EVENT = "lunyu-player-avatar";
const TEST_NAME_RE = /刘国欢|劉國歡|liuguohuan/i;
const AVATAR_URL_RE = /^(https?:\/\/|data:image\/(?:png|jpe?g|gif|webp|svg\+xml);base64,)/i;

export type LeaderboardKind = "user" | "guest";

export interface LeaderboardEntry {
  id: string;
  kind: LeaderboardKind;
  name: string;
  slug?: string;
  avatar?: string;
  mastered: number;
  gates: number;
  totalGates: number;
  percent: number;
  bestStreak: number;
  focus: number;
  updatedAt: number;
}

export interface GlobalLeaderboardEntry {
  id: string;
  kind: "global";
  name: string;
  slug?: string;
  avatar?: string;
  mastered: number;
  gates: number;
  totalGates: number;
  percent: number;
  bestStreak: number;
  focus: number;
  updatedAt: string;
}

interface GuestProfile {
  id: string;
  name: string;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage may be disabled */
  }
}

function isTestName(value: unknown): boolean {
  return typeof value === "string" && TEST_NAME_RE.test(value);
}

function sanitizeName(value: string): string {
  return value.replace(/\s+/g, " ").trim().slice(0, 24);
}

function aliasMap(): Record<string, string> {
  const aliases = readJson<Record<string, string>>(ALIAS_KEY, {});
  let changed = false;
  for (const [key, value] of Object.entries(aliases)) {
    if (!value || isTestName(value) || isTestName(key)) {
      delete aliases[key];
      changed = true;
    }
  }
  if (changed) writeJson(ALIAS_KEY, aliases);
  return aliases;
}

function aliasFor(id: string): string {
  return aliasMap()[id] ?? "";
}

function avatarMap(): Record<string, string> {
  const avatars = readJson<Record<string, string>>(AVATAR_KEY, {});
  let changed = false;
  for (const [key, value] of Object.entries(avatars)) {
    if (!sanitizeAvatar(value) || isTestName(key)) {
      delete avatars[key];
      changed = true;
    }
  }
  if (changed) writeJson(AVATAR_KEY, avatars);
  return avatars;
}

function avatarFor(id: string): string {
  return avatarMap()[id] ?? "";
}

export function sanitizeAvatar(value: string): string {
  const next = value.trim();
  return AVATAR_URL_RE.test(next) ? next.slice(0, 120_000) : "";
}

export function saveAvatar(id: string, value: string): string {
  const nextAvatar = sanitizeAvatar(value);
  const avatars = avatarMap();
  if (nextAvatar) {
    avatars[id] = nextAvatar;
  } else {
    delete avatars[id];
  }
  writeJson(AVATAR_KEY, avatars);
  return avatars[id] ?? "";
}

export function clearAvatar(id: string): void {
  const avatars = avatarMap();
  delete avatars[id];
  writeJson(AVATAR_KEY, avatars);
}

export function saveDisplayAlias(id: string, name: string): string {
  const nextName = sanitizeName(name);
  const aliases = aliasMap();
  if (!nextName || isTestName(nextName)) {
    delete aliases[id];
  } else {
    aliases[id] = nextName;
  }
  writeJson(ALIAS_KEY, aliases);
  return aliases[id] ?? "";
}

export function loadActivePlayerName(): string {
  const name = sanitizeName(readJson<string>(ACTIVE_NAME_KEY, ""));
  return name && !isTestName(name) ? name : "學子";
}

export function saveActivePlayerName(name: string): string {
  const nextName = sanitizeName(name) || "學子";
  writeJson(ACTIVE_NAME_KEY, nextName);
  window.dispatchEvent(new CustomEvent(PLAYER_NAME_EVENT, { detail: nextName }));
  return nextName;
}

export function loadActivePlayerAvatar(): string {
  return sanitizeAvatar(readJson<string>(ACTIVE_AVATAR_KEY, ""));
}

export function saveActivePlayerAvatar(avatar: string): string {
  const nextAvatar = sanitizeAvatar(avatar);
  if (nextAvatar) {
    writeJson(ACTIVE_AVATAR_KEY, nextAvatar);
  } else {
    try {
      localStorage.removeItem(ACTIVE_AVATAR_KEY);
    } catch {
      /* storage may be disabled */
    }
  }
  window.dispatchEvent(new CustomEvent(PLAYER_AVATAR_EVENT, { detail: nextAvatar }));
  return nextAvatar;
}

function guestProfile(): GuestProfile {
  const existing = readJson<GuestProfile | null>(GUEST_KEY, null);
  if (existing?.id && existing.name && !isTestName(existing.name) && !isTestName(existing.id)) return existing;
  const id = `guest-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const profile = {
    id,
    name: `訪客 ${id.slice(-4).toUpperCase()}`,
  };
  writeJson(GUEST_KEY, profile);
  return profile;
}

export function loadLeaderboard(): LeaderboardEntry[] {
  const rows = readJson<LeaderboardEntry[]>(BOARD_KEY, []);
  if (!Array.isArray(rows)) return [];
  const clean = rows.filter((row) => !isTestName(row.name) && !isTestName(row.slug) && !isTestName(row.id));
  if (clean.length !== rows.length) writeJson(BOARD_KEY, clean);
  return clean.sort(compareEntries).slice(0, 40);
}

export function upsertLeaderboard(entry: LeaderboardEntry): LeaderboardEntry[] {
  const rows = loadLeaderboard().filter((row) => row.id !== entry.id);
  rows.push(entry);
  rows.sort(compareEntries);
  const next = rows.slice(0, 40);
  writeJson(BOARD_KEY, next);
  return next;
}

export function splitLeaderboard(rows: LeaderboardEntry[]) {
  return {
    users: rows.filter((row) => row.kind === "user").slice(0, 5),
    guests: rows.filter((row) => row.kind === "guest").slice(0, 5),
  };
}

function isGlobalLeaderboardEntry(value: unknown): value is GlobalLeaderboardEntry {
  if (!value || typeof value !== "object") return false;
  const entry = value as Partial<GlobalLeaderboardEntry>;
  return (
    typeof entry.id === "string"
    && entry.kind === "global"
    && typeof entry.name === "string"
    && typeof entry.gates === "number"
    && !isTestName(entry.name)
    && !isTestName(entry.slug)
    && !isTestName(entry.id)
  );
}

export async function loadGlobalLeaderboard(limit = 10): Promise<GlobalLeaderboardEntry[]> {
  if (import.meta.env.DEV) return [];
  try {
    const response = await fetch(`/api/leaderboard?limit=${encodeURIComponent(String(limit))}`, {
      headers: { accept: "application/json" },
    });
    if (!response.ok) return [];
    const data = (await response.json()) as { entries?: unknown };
    return Array.isArray(data.entries) ? data.entries.filter(isGlobalLeaderboardEntry).slice(0, limit) : [];
  } catch {
    return [];
  }
}

export function makeLeaderboardEntry(
  session: BdfzSession | null,
  summary: ProgressSummary,
  progress: ProgressState,
): LeaderboardEntry {
  if (session?.authenticated && session.user?.slug) {
    const id = `user:${session.user.slug}`;
    return {
      id,
      kind: "user",
      name: aliasFor(id) || displayName(session.user),
      slug: session.user.slug,
      avatar: avatarFor(id) || avatarUrl(session.user),
      mastered: summary.mastered,
      gates: summary.gates,
      totalGates: summary.totalGates,
      percent: summary.percent,
      bestStreak: progress.bestStreak,
      focus: progress.focus,
      updatedAt: Date.now(),
    };
  }

  const guest = guestProfile();
  const id = `guest:${guest.id}`;
  return {
    id,
    kind: "guest",
    name: aliasFor(id) || guest.name,
    avatar: avatarFor(id),
    mastered: summary.mastered,
    gates: summary.gates,
    totalGates: summary.totalGates,
    percent: summary.percent,
    bestStreak: progress.bestStreak,
    focus: progress.focus,
    updatedAt: Date.now(),
  };
}

export function compareEntries(a: LeaderboardEntry, b: LeaderboardEntry): number {
  return (
    b.gates - a.gates
    || b.mastered - a.mastered
    || b.bestStreak - a.bestStreak
    || b.focus - a.focus
    || b.updatedAt - a.updatedAt
  );
}

export function relativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  if (diff < 60_000) return "剛剛";
  if (diff < 3_600_000) return `${Math.max(1, Math.floor(diff / 60_000))} 分前`;
  if (diff < 86_400_000) return `${Math.max(1, Math.floor(diff / 3_600_000))} 時前`;
  return `${Math.max(1, Math.floor(diff / 86_400_000))} 日前`;
}
